import { Link, Outlet, createFileRoute, useRouterState } from '@tanstack/react-router';
import {
  ArrowRight,
  BarChart3,
  Check,
  CircleDollarSign,
  FileSearch,
  LayoutTemplate,
  MessageCircle,
  Search,
  ShieldCheck,
  Sparkles,
  Target,
} from 'lucide-react';

import { ContactSection } from '@/components/contact-section';
import { PublicBridgeHero } from '@/components/public-bridge-hero';
import { PublicFloatingWhatsApp } from '@/components/public-floating-whatsapp';
import { PublicSiteHeader } from '@/components/public-site-header';
import { canonicalSiteUrl } from '@/config';

import { whatsappUrlEn as whatsappUrl } from '@/lib/whatsapp';
const emailAddress = 'info@doqcreative.com';

const services = [
  {
    title: 'Website design',
    body: 'Many businesses discover too late that their new site has weak content order and no clear focus. We plan the service message and enquiry path before building the pages.',
    icon: LayoutTemplate,
    href: '/en/services/web-design',
  },
  {
    title: 'SEO services',
    body: 'Some sites publish regularly but never connect search questions to clear service pages. We link keywords, page roles, content updates and ongoing optimisation.',
    icon: Search,
    href: '/en/services/seo',
  },
  {
    title: 'Google Ads search management',
    body: 'Keyword intent, ad copy, landing pages and enquiry tracking are planned around what customers are actively searching for.',
    icon: BarChart3,
    href: '/en/services/google-ads',
  },
  {
    title: 'Meta Ads management',
    body: 'Facebook, Instagram and Threads ads connect audience, creative direction, remarketing, landing pages and measurable enquiry actions.',
    icon: CircleDollarSign,
    href: '/en/services/meta-ads',
  },
  {
    title: 'Custom ERP systems',
    body: 'Turn scattered customer, sales, order, inventory, project, employee, attendance, roster and payroll data into an operations system built for your team.',
    icon: ShieldCheck,
    href: '/en/services/erp-system-development',
  },
  {
    title: 'WhatsApp chatbot customisation',
    body: 'Connect automated replies, FAQ, lead collection, booking and human handover into a clearer WhatsApp enquiry flow with less repeated reply work.',
    icon: MessageCircle,
    href: '/en/services/whatsapp-chatbot-development',
  },
];

const reasons = [
  {
    title: 'Planning before design',
    body: 'We start with your services, customers and enquiry path before deciding the layout. A good website is usually a business clarity problem first.',
    icon: FileSearch,
  },
  {
    title: 'Website, SEO and ads connected',
    body: 'Pages, keywords, ads and CTAs are planned together, so paid and organic traffic have somewhere relevant to land.',
    icon: Sparkles,
  },
  {
    title: 'Built for Hong Kong SMEs',
    body: 'The work suits local service companies, professional firms, new brands and businesses that need a practical website without a bloated process.',
    icon: MessageCircle,
  },
  {
    title: 'Mobile enquiry flow first',
    body: 'Many local enquiries start on mobile search. WhatsApp buttons, page length, image weight and CTA placement are treated as core design decisions.',
    icon: BarChart3,
  },
];

const caseTypes = [
  {
    title: 'Local service website',
    body: 'For repair, engineering, beauty, cleaning, education and other local services. The page needs to explain scope, pricing direction, service area and WhatsApp enquiry clearly.',
    image: '/images/case-local-service.webp',
    srcSet: '/images/case-local-service-640.webp 640w, /images/case-local-service-800.webp 800w, /images/case-local-service.webp 1066w',
    alt: 'Local service website concept showing service sections, enquiry buttons and mobile layout',
    href: '/en/services/local-service-website',
  },
  {
    title: 'Professional service website',
    body: 'For consultants, accounting, legal, healthcare and B2B teams. The structure should show expertise, service process, case proof and trust signals without making the page hard to read.',
    image: '/images/case-professional-service.webp',
    srcSet: '/images/case-professional-service-640.webp 640w, /images/case-professional-service-800.webp 800w, /images/case-professional-service.webp 1067w',
    alt: 'Professional service website concept showing service structure and desktop mobile layouts',
    href: '/en/services/professional-service-website',
  },
  {
    title: 'Google / Meta Ads landing page',
    body: 'For Google Ads, Meta Ads or short campaigns. One service, one clear CTA, Meta Pixel and tracking set up properly, so you can judge whether the ad traffic is producing enquiries.',
    image: '/images/case-google-ads.webp',
    srcSet: '/images/case-google-ads-640.webp 640w, /images/case-google-ads-800.webp 800w, /images/case-google-ads.webp 1066w',
    alt: 'Google Ads and Meta Ads landing page concept showing campaign traffic, conversion form and tracking',
    href: '/en/services/google-ads-landing-page',
  },
  {
    title: 'Website redesign',
    body: 'For companies with an old website, scattered content or weak mobile experience. We reorganise page order, service wording, SEO basics and enquiry routes.',
    image: '/images/case-website-refresh.webp',
    srcSet: '/images/case-website-refresh-640.webp 640w, /images/case-website-refresh.webp 761w',
    alt: 'Website redesign concept showing an anonymous before and after website structure',
    href: '/en/services/website-redesign',
  },
];

const seoItems = [
  {
    title: 'What customers search',
    body: 'Separate brand terms, service terms and problem terms instead of sending every search to the same page.',
    icon: Search,
  },
  {
    title: 'Which page should receive them',
    body: 'The homepage, service pages, articles and landing pages should each have a clear job.',
    icon: LayoutTemplate,
  },
  {
    title: 'What happens after they read',
    body: 'CTA, FAQ, cases and contact points need to appear where a serious visitor expects the next step.',
    icon: Target,
  },
];

const testimonials = [
  {
    quote:
      'We first thought we only needed a nicer website. The useful part was actually the early planning: service order, enquiry flow and clearer wording.',
    name: 'Local service business owner',
    detail: 'Website design',
  },
  {
    quote:
      'Our old website had a lot of content, but search and ads were not landing well. Doq Creative helped split the services into clearer pages.',
    name: 'Professional service team',
    detail: 'Company website refresh',
  },
  {
    quote:
      'The communication was direct. They pointed out what did not need to be on the page and where the mobile enquiry path should be clearer.',
    name: 'New brand founder',
    detail: 'Brand website and content planning',
  },
];

const pricingPlans = [
  {
    name: 'One-page website',
    price: 'HK$999',
    unit: 'from',
    eyebrow: 'Landing Page / One-page site',
    badge: 'Trial offer',
    description:
      'For one service, a campaign page, an ads landing page, or a simple first website that explains your business clearly.',
    features: [
      'One-page layout design and build',
      'Mobile and desktop responsive production',
      'Basic service copy organisation',
      'WhatsApp enquiry button',
      'Basic SEO title and description',
      'Pre-launch speed and display check',
      'One year website hosting',
    ],
    addons: ['Additional language +HK$500'],
    note: 'Best for small scopes where you want a usable page quickly.',
  },
  {
    name: 'Company website + CMS',
    price: 'HK$3,999',
    unit: 'from',
    eyebrow: 'Company website / CMS',
    description:
      'For businesses that need multiple pages, content management and a proper company structure.',
    features: [
      'Homepage, about, service and contact pages',
      'CMS content management',
      'Service page structure and content organisation',
      'Mobile and desktop responsive production',
      'Basic SEO structure setup',
      'Form or WhatsApp enquiry path',
      'Image compression and basic speed optimisation',
      'Google Analytics / Search Console basic setup',
      'Launch support and basic handover',
      'One year website hosting',
    ],
    note: 'A common fit for company website refreshes, professional services and local service brands.',
    featured: true,
  },
  {
    name: 'Google Ads / Meta Ads management',
    price: 'Quoted monthly',
    unit: 'monthly',
    eyebrow: 'Google Ads / Meta Ads management',
    description:
      'For businesses that need ongoing Google Search, Facebook, Instagram or Threads advertising, with landing pages, enquiry tracking and budget optimisation handled as one scope.',
    features: [
      'Google Ads keywords, campaigns and ad-copy management',
      'Meta audience, creative direction and remarketing planning',
      'Landing page, Meta Pixel and enquiry-tracking checks',
      'Ongoing optimisation by searches, audiences, creative and lead quality',
      'Monthly performance review and next-step recommendations',
    ],
    note: 'Media spend is separate; monthly scope follows advertising channels, campaign count, media scope and optimisation cadence.',
  },
];

const faqs = [
  {
    q: 'Is the planning stage similar to website consulting?',
    a: 'In part, but we do not stop at advice. We turn the agreed direction into a sitemap, content structure, design, SEO basics and a website ready to launch.',
  },
  {
    q: 'How long does it take to build a website?',
    a: 'A simple one-page site can often be completed within a week when the direction and materials are clear. Larger company websites depend on the number of pages, copy and CMS requirements.',
  },
  {
    q: 'Can you rebuild only part of my existing website?',
    a: 'Yes. You may not need to rebuild everything. We can start with the homepage, key service pages or an ad landing page, then address the areas that affect enquiries first.',
  },
  {
    q: 'Should SEO be considered only after the website is finished?',
    a: 'No. SEO should be planned with the website, including keywords, page roles, titles, content and internal links. Waiting until after launch usually creates more rework.',
  },
  {
    q: 'Do Google Ads or Meta Ads require a large monthly budget?',
    a: 'Not always. The right budget depends on competition, service value and campaign goals. A clear landing page, Meta Pixel and enquiry tracking should be in place before increasing spend.',
  },
  {
    q: 'Can we start with just one landing page?',
    a: 'Yes. This works well for a new service, short campaign or market test. Even a single page should cover the customer problem, your service, proof points, FAQs and a WhatsApp enquiry option.',
  },
  { q: 'Does a website need ongoing SEO after launch?', a: 'If organic search matters, the site should be reviewed over time. Basic setup is only the starting point; search terms, service content, internal links and technical issues continue to change.' },
  { q: 'Should I choose a one-page or company website?', a: 'A one-page site suits one service, campaign or early market test. A multi-page company site is a better fit when you have several services, cases, articles or regular updates.' },
  { q: 'Should I redesign part of a site or rebuild everything?', a: 'If the platform is stable, key pages can be improved first. If the structure, CMS, mobile experience and content are all outdated, a full rebuild is often more efficient.' },
  { q: 'Can SEO and advertising run at the same time?', a: 'Yes. Ads can test demand and messages quickly, while SEO builds longer-term visibility. They should share clear service pages and enquiry tracking while keeping budgets separate.' },
  { q: 'What does custom ERP development include?', a: 'It can include workflow analysis, prototypes, core module development, testing, launch, data migration planning and ongoing feature iteration. The exact scope follows the business process and integrations involved.' },
  { q: 'Can a WhatsApp chatbot handle bookings?', a: 'It can handle common questions, lead collection, available-time rules, confirmation and human handover. Calendar, CRM and other integrations depend on API access, account permissions and the confirmed workflow.' },
  { q: 'Do I need all copy and images ready before we start?', a: 'No. Start with company information, core services, target clients and reference sites. We identify the missing material and organise it into a usable page structure.' },
];

function WhatsAppButton({ className = '' }: { className?: string }) {
  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center justify-center gap-2 bg-[#815321] font-semibold text-white transition hover:bg-[#6f451c] active:translate-y-px ${className}`}
    >
      WhatsApp enquiry
      <ArrowRight className="size-4" strokeWidth={1.8} />
    </a>
  );
}

function EnglishPage() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  if (pathname.replace(/\/$/, '') !== '/en') return <Outlet />;

  return (
    <div className="min-h-screen bg-doq-canvas text-doq-ink">
      <div className="mx-auto max-w-[1540px] border-x border-[#143d35]/16 bg-doq-surface shadow-[0_0_0_1px_rgba(20,61,53,0.04)]">
        <PublicSiteHeader locale="en" enHref="/en" zhHref="/" />

        <main id="main-content">
          <PublicBridgeHero
            title={'Websites that are easier to\nunderstand, find and contact'}
            description="Web design, SEO and Google Ads, taking your idea through to real enquiries."
            primaryLabel="WhatsApp enquiry"
            primaryHref={whatsappUrl}
            secondaryLabel="View services"
            secondaryHref="#services"
          />

          <section id="services" className="bg-doq-surface px-4 py-10 sm:px-8 sm:py-12 lg:px-24">
            <div className="mb-6 flex flex-col justify-between gap-3 md:mb-8 md:flex-row md:items-end">
              <h2 className="text-[30px] font-semibold leading-tight tracking-[-0.05em] sm:text-4xl">Services</h2>
              <p className="max-w-xl text-sm leading-7 text-[#344840]">
                You can start with a single landing page, rebuild a company website, or connect SEO, Google Ads and Meta Ads to a clearer enquiry flow.
              </p>
            </div>
            <div className="-mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-2 sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-5 sm:overflow-visible sm:px-0 sm:pb-0 lg:grid-cols-4">
              {services.map((service) => (
                <Link
                  key={service.title}
                  to={service.href}
                  className="group min-w-[78vw] snap-start border border-[#143d35]/18 bg-doq-card p-5 transition hover:-translate-y-1 hover:bg-white sm:min-w-0 sm:p-6 lg:min-h-[250px]"
                >
                  <service.icon className="size-7 text-[#143d35] sm:size-8" strokeWidth={1.45} />
                  <h3 className="mt-6 text-lg font-semibold tracking-[-0.04em] sm:mt-8 sm:text-xl">{service.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-[#344840] sm:mt-4 sm:leading-7">{service.body}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#173d35] transition group-hover:text-[#815321] sm:mt-6">
                    View service
                    <ArrowRight className="size-4 transition group-hover:translate-x-1" />
                  </span>
                </Link>
              ))}
            </div>
          </section>

          <section className="bg-doq-surface px-4 py-10 sm:px-8 sm:py-12 lg:px-24">
            <div className="grid gap-7 lg:grid-cols-[0.78fr_1.22fr] lg:gap-10">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#815321]">Why Doq Creative</p>
                <h2 className="mt-3 text-balance text-[30px] font-semibold leading-tight tracking-[-0.05em] sm:mt-4 sm:text-4xl">
                  Design is useful only when the direction is clear
                </h2>
                <p className="mt-4 max-w-md text-[15px] leading-7 text-[#344840] sm:mt-5 sm:text-base sm:leading-8">
                  A website often fails because the message, page order, trust signals or enquiry route were never planned properly. We fix that before polishing the visuals.
                </p>
                <div className="relative mx-auto mt-7 max-w-[240px] sm:mt-12 sm:max-w-[390px] lg:ml-4 lg:mt-16">
                  <div className="absolute inset-x-10 bottom-8 h-20 rounded-full bg-[#143d35]/8 blur-2xl" aria-hidden="true" />
                  <img
                    src="/images/doq-thinking-consultant-420.webp"
                    srcSet="/images/doq-thinking-consultant-420.webp 420w, /images/doq-thinking-consultant-640.webp 640w, /images/doq-thinking-consultant.webp 720w"
                    sizes="(min-width: 640px) 380px, 240px"
                    alt="Doq Creative consultant thinking through website direction"
                    width="420"
                    height="492"
                    className="relative mx-auto h-auto w-full max-w-[240px] object-contain sm:max-w-[380px]"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>
              <div className="grid gap-px bg-[#143d35]/14 sm:grid-cols-2">
                {reasons.map((reason) => (
                  <article key={reason.title} className="bg-doq-card p-5 sm:p-7">
                    <reason.icon className="size-7 text-[#143d35] sm:size-8" strokeWidth={1.45} />
                    <h3 className="mt-4 text-lg font-semibold tracking-[-0.04em] sm:mt-6 sm:text-xl">{reason.title}</h3>
                    <p className="mt-2 text-sm leading-6 text-[#344840] sm:mt-3 sm:leading-7">{reason.body}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section id="pricing" className="bg-doq-surface px-4 py-10 sm:px-8 sm:py-12 lg:px-24">
            <div className="mb-7 grid gap-4 sm:mb-9 lg:grid-cols-[0.78fr_1.22fr] lg:items-end lg:gap-6">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#815321]">Pricing</p>
                <h2 className="mt-3 text-balance text-[30px] font-semibold leading-tight tracking-[-0.05em] sm:mt-4 sm:text-4xl md:text-5xl">
                  Start with a clear budget direction
                </h2>
              </div>
              <p className="max-w-2xl text-[15px] leading-7 text-[#344840] sm:text-base sm:leading-8">
                Page count, copywriting, CMS, languages and tracking all affect the final scope. These starting packages make it easier to judge the likely budget before we talk.
              </p>
            </div>
            <div className="-mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-2 sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-px sm:bg-[#143d35]/14 sm:px-0 sm:pb-0 lg:grid-cols-3">
              {pricingPlans.map((plan) => (
                <article
                  key={plan.name}
                  className={`relative flex min-w-[84vw] snap-start flex-col border border-[#143d35]/14 p-5 sm:min-w-0 sm:border-0 sm:p-8 ${
                    plan.featured ? 'bg-[#063d36] text-[#fbf5ea]' : 'bg-doq-card'
                  }`}
                >
                  <div className="sm:min-h-[238px]">
                    <div className="flex min-h-7 items-start justify-between gap-5">
                      <p className={`pr-2 text-xs font-semibold uppercase tracking-[0.18em] ${plan.featured ? 'text-[#d9a267]' : 'text-[#815321]'}`}>
                        {plan.eyebrow}
                      </p>
                      {plan.badge ? (
                        <span className="shrink-0 bg-[#815321] px-3 py-1 text-xs font-semibold tracking-[0.12em] text-white">
                          {plan.badge}
                        </span>
                      ) : plan.featured ? (
                        <span className="shrink-0 border border-[#d9a267]/60 px-3 py-1 text-xs font-semibold tracking-[0.12em] text-[#f0c58b]">
                          Common choice
                        </span>
                      ) : null}
                    </div>
                    <h3 className="mt-5 text-xl font-semibold tracking-[-0.05em] sm:mt-6 sm:text-2xl">{plan.name}</h3>
                    <div className="mt-5 flex items-end gap-2 sm:mt-7">
                      <span className="text-4xl font-semibold tracking-[-0.07em] sm:text-5xl">{plan.price}</span>
                      <span className={`pb-2 text-sm ${plan.featured ? 'text-[#eef5ea]' : 'text-[#344840]'}`}>{plan.unit}</span>
                    </div>
                    <p className={`mt-5 text-sm leading-7 ${plan.featured ? 'text-[#eef5ea]' : 'text-[#344840]'}`}>
                      {plan.description}
                    </p>
                  </div>
                  <ul className="mt-6 space-y-3 sm:mt-7 sm:min-h-[288px]">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex gap-3 text-sm leading-6">
                        <Check className={`mt-1 size-4 shrink-0 ${plan.featured ? 'text-[#d9a267]' : 'text-[#815321]'}`} strokeWidth={1.8} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  {plan.addons ? (
                    <div className={`mt-6 border px-4 py-4 text-sm sm:mt-7 ${plan.featured ? 'border-white/22 text-[#eef5ea]' : 'border-[#143d35]/14 text-[#344840]'}`}>
                      <p className="font-semibold tracking-[-0.02em] text-inherit">Add-ons</p>
                      <div className="mt-3 grid gap-2">
                        {plan.addons.map((addon) => (
                          <p key={addon}>{addon}</p>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  <div className="mt-auto pt-6 sm:pt-8">
                    <p className={`border-t pt-5 text-sm leading-7 ${plan.featured ? 'border-white/22 text-[#eef5ea]' : 'border-[#143d35]/14 text-[#344840]'}`}>
                      {plan.note}
                    </p>
                    <a
                      href={whatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`mt-6 inline-flex h-12 w-full items-center justify-center gap-2 px-5 text-sm font-semibold transition sm:mt-7 ${
                        plan.featured
                          ? 'bg-[#d9a267] text-[#063d36] hover:bg-[#efc98f]'
                          : 'bg-[#063d36] text-[#fbf5ea] hover:bg-[#0b4a42]'
                      }`}
                    >
                      WhatsApp enquiry
                      <ArrowRight className="size-4" strokeWidth={1.8} />
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section id="seo" className="bg-doq-surface px-4 py-10 sm:px-8 sm:py-14 lg:px-24">
            <div className="grid gap-7 lg:grid-cols-[0.76fr_1.24fr] lg:gap-10">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#815321]">Search before layout</p>
                <h2 className="mt-3 text-balance text-[30px] font-semibold leading-tight tracking-[-0.05em] sm:mt-4 sm:text-4xl md:text-5xl">
                  Before designing the page, think about how customers will find you
                </h2>
                <p className="mt-4 max-w-xl text-[15px] leading-7 text-[#344840] sm:mt-6 sm:text-base sm:leading-8">
                  Web design in Hong Kong is not only about layout. For many SMEs, the website needs to explain the service, help Google understand the page, and make enquiries easy.
                </p>
                <p className="mt-3 max-w-xl text-[15px] leading-7 text-[#344840] sm:mt-4 sm:text-base sm:leading-8">
                  We plan website design, SEO, Google Ads and Meta Ads together so search and social traffic have the right message, page and enquiry path.
                </p>
                <WhatsAppButton className="mt-6 h-12 w-full px-5 text-sm sm:mt-8 sm:w-auto" />
              </div>
              <div className="grid gap-4 lg:grid-cols-[0.9fr_1.1fr] lg:gap-5">
                <div className="grid border border-[#143d35]/18">
                  {seoItems.map((item) => (
                    <article key={item.title} className="flex gap-4 border-b border-[#143d35]/14 bg-doq-card p-5 last:border-b-0 sm:gap-5 sm:p-6">
                      <item.icon className="mt-1 size-5 shrink-0 text-[#815321] sm:size-6" strokeWidth={1.55} />
                      <div>
                        <h3 className="font-semibold tracking-[-0.03em]">{item.title}</h3>
                        <p className="mt-1 text-sm leading-6 text-[#344840]">{item.body}</p>
                      </div>
                    </article>
                  ))}
                </div>
                <div className="relative overflow-hidden border border-[#143d35]/18 bg-[#123f37] p-5 text-[#fbf5ea] sm:p-6">
                  <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:42px_42px]" />
                  <div className="relative">
                    <div className="flex items-center justify-between border-b border-white/16 pb-4">
                      <span className="text-sm font-semibold">Search to enquiry path</span>
                      <span className="text-xs uppercase tracking-[0.18em] text-[#d9a267]">SEO / Ads / CTA</span>
                    </div>
                    <div className="mt-6 space-y-4 sm:mt-7 sm:space-y-5">
                      {[
                        ['Search', 'Service keywords, location terms and customer questions'],
                        ['Understand', 'Clear service scope, process and suitable customer type'],
                        ['Trust', 'Cases, FAQ, process and real contact details'],
                        ['Enquire', 'Use WhatsApp to ask for pricing, booking or next steps'],
                      ].map(([title, body], index) => (
                        <div key={title} className="grid grid-cols-[38px_1fr] gap-3 sm:grid-cols-[44px_1fr] sm:gap-4">
                          <span className="grid size-8 place-items-center rounded-full bg-[#d9a267] text-xs font-semibold text-[#123f37] sm:size-9">
                            {String(index + 1).padStart(2, '0')}
                          </span>
                          <div className="border-b border-white/14 pb-3 sm:pb-4">
                            <p className="font-semibold tracking-[-0.02em]">{title}</p>
                            <p className="mt-1 text-sm leading-6 text-[#eef5ea]">{body}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 grid grid-cols-3 gap-2 text-center text-[11px] text-[#eef5ea] sm:mt-7 sm:text-xs">
                      <span className="border border-white/16 px-2 py-3">Keywords</span>
                      <span className="border border-white/16 px-2 py-3">Content structure</span>
                      <span className="border border-white/16 px-2 py-3">Tracking</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section id="cases" className="bg-doq-surface px-4 py-10 sm:px-8 sm:py-12 lg:px-24">
            <div className="mb-6 flex flex-col justify-between gap-4 md:mb-8 md:flex-row md:items-end">
              <h2 className="text-[30px] font-semibold leading-tight tracking-[-0.05em] sm:text-4xl">Different businesses need different website routes</h2>
              <Link to="/en/cases" className="inline-flex h-12 items-center justify-center gap-2 bg-[#063d36] px-5 text-sm font-semibold text-[#fbf5ea] transition hover:bg-[#0b4a42]">
                View real cases
                <ArrowRight className="size-4" />
              </Link>
            </div>
            <div className="-mx-4 flex snap-x gap-4 overflow-x-auto px-4 pb-2 sm:mx-0 sm:grid sm:grid-cols-2 sm:gap-5 sm:overflow-visible sm:px-0 sm:pb-0 lg:grid-cols-4">
              {caseTypes.map((item) => (
                <article key={item.title} className="group min-w-[78vw] snap-start sm:min-w-0">
                  <div className="overflow-hidden border border-[#143d35]/18 bg-[#efe8dc]">
                    <Link to={item.href} aria-label={`View ${item.title} service`}>
                      <img
                        src={item.image}
                        srcSet={item.srcSet}
                        sizes="(min-width: 1280px) 25vw, (min-width: 640px) 50vw, 78vw"
                        alt={item.alt}
                        width="800"
                        height="533"
                        className="aspect-[4/3] w-full object-cover transition duration-500 group-hover:scale-[1.035]"
                        loading="lazy"
                        decoding="async"
                      />
                    </Link>
                  </div>
                  <h3 className="mt-3 text-base font-semibold tracking-[-0.04em] sm:mt-4 sm:text-lg">
                    <Link to={item.href} className="transition hover:text-[#815321]">
                      {item.title}
                    </Link>
                  </h3>
                  <p className="mt-1 line-clamp-2 text-sm leading-6 text-[#344840] sm:line-clamp-none">{item.body}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="bg-doq-surface px-4 py-10 sm:px-8 sm:py-12 lg:px-24">
            <div className="grid gap-7 lg:grid-cols-[0.78fr_1.22fr] lg:gap-9">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#815321]">Client feedback</p>
                <h2 className="mt-3 text-balance text-[30px] font-semibold leading-tight tracking-[-0.05em] sm:mt-4 sm:text-4xl">
                  What clients usually notice
                </h2>
                <p className="mt-4 max-w-md text-[15px] leading-7 text-[#344840] sm:mt-5 sm:text-base sm:leading-8">
                  A good website should be easier for visitors to understand, easier for the team to use, and easier for enquiries to follow up.
                </p>
              </div>
              <div className="-mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-2 sm:mx-0 sm:grid sm:grid-cols-3 sm:gap-px sm:bg-[#143d35]/14 sm:overflow-visible sm:px-0 sm:pb-0">
                {testimonials.map((item) => (
                  <article key={item.name} className="min-w-[82vw] snap-start border border-[#143d35]/14 bg-doq-card p-5 sm:min-w-0 sm:border-0 sm:p-7">
                    <div className="text-3xl font-semibold leading-none text-[#815321] sm:text-4xl">“</div>
                    <p className="mt-4 text-sm leading-6 text-[#354a43] sm:mt-5 sm:leading-7">{item.quote}</p>
                    <div className="mt-6 border-t border-[#143d35]/14 pt-4 sm:mt-8 sm:pt-5">
                      <p className="font-semibold tracking-[-0.03em] text-[#102d28]">{item.name}</p>
                      <p className="mt-1 text-sm text-[#3f514a]">{item.detail}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section id="faq" className="bg-doq-surface px-4 py-10 sm:px-8 sm:py-12 lg:px-24">
            <h2 className="text-[30px] font-semibold leading-tight tracking-[-0.05em] sm:text-4xl">FAQ</h2>
            <div className="mt-6 grid gap-3 md:grid-cols-2">
              {faqs.map((faq, index) => (
                <details key={faq.q} className="group self-start border border-[#143d35]/18 bg-doq-card" open={index === 0}>
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-4 py-4 text-left text-sm font-semibold tracking-[-0.02em] text-[#143d35] outline-none transition hover:bg-[#f3eadc] focus-visible:ring-2 focus-visible:ring-[#d9a267] focus-visible:ring-offset-2 focus-visible:ring-offset-[#fbf5ea] sm:gap-5 sm:px-6 sm:text-base [&::-webkit-details-marker]:hidden">
                    <span>{faq.q}</span>
                    <ArrowRight className="size-4 shrink-0 text-[#815321] transition group-open:rotate-90" />
                  </summary>
                  <div className="px-4 pb-5 text-sm leading-6 text-[#344840] sm:px-6 sm:leading-7">{faq.a}</div>
                </details>
              ))}
            </div>
          </section>

          <ContactSection
            locale="en"
            title="Have a website idea but not the full plan yet?"
            description="Send your current website, a competitor site, or a short description of the service you want to promote. We will help you decide whether to rebuild the site, improve the content, plan SEO, or start with a landing page."
          />
        </main>

        <footer className="border-t border-[#143d35]/14 px-5 py-6 sm:px-8 lg:px-24">
          <div className="flex flex-col gap-3 text-center text-sm text-[#344840] md:flex-row md:items-center md:justify-between md:text-left">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <Link to="/en" className="font-semibold text-[#102d28]">Doq Creative Limited</Link>
              <Link to="/en/terms" className="hover:text-[#815321]">Terms of Service</Link>
              <Link to="/en/privacy" className="hover:text-[#815321]">Privacy Policy</Link>
            </div>
            <p>© 2026 Doq Creative Limited. All rights reserved.</p>
          </div>
        </footer>
      </div>
      <PublicFloatingWhatsApp locale="en" />
    </div>
  );
}

export const Route = createFileRoute('/en')({
  head: () => {
    const title = 'Hong Kong Web Design and SEO | Doq Creative';
    const description =
      'Doq Creative helps Hong Kong SMEs clarify their direction, then connect it to a practical website, SEO structure, Google Ads, Meta Ads and clear enquiry paths.';
    const pageUrl = `${canonicalSiteUrl}/en`;
    const ogImageUrl = `${canonicalSiteUrl}/og-image.jpg`;

    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: pageUrl },
        { property: 'og:image', content: ogImageUrl },
        { property: 'og:image:width', content: '1200' },
        { property: 'og:image:height', content: '630' },
        { property: 'og:image:alt', content: 'Doq Creative Hong Kong web design and SEO preview' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: ogImageUrl },
        {
          'script:ld+json': {
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': ['Organization', 'LocalBusiness', 'ProfessionalService'],
                '@id': `${canonicalSiteUrl}/#organization`,
                name: 'Doq Creative',
                alternateName: '渡橋',
                url: `${canonicalSiteUrl}/`,
                image: ogImageUrl,
                email: emailAddress,
                telephone: '+85295946956',
                areaServed: { '@type': 'AdministrativeArea', name: 'Hong Kong' },
                address: {
                  '@type': 'PostalAddress',
                  streetAddress: "Room A, 19/F, Max Share Centre, 367-373 King's Road",
                  addressLocality: 'North Point',
                  addressRegion: 'Hong Kong',
                  addressCountry: 'HK',
                },
                description,
              },
              {
                '@type': 'WebPage',
                '@id': `${pageUrl}#webpage`,
                name: title,
                url: pageUrl,
                inLanguage: 'en',
                isPartOf: { '@type': 'WebSite', '@id': `${canonicalSiteUrl}/#website` },
              },
              {
                '@type': 'FAQPage',
                '@id': `${pageUrl}#faq`,
                inLanguage: 'en',
                mainEntity: faqs.map((faq) => ({
                  '@type': 'Question',
                  name: faq.q,
                  acceptedAnswer: { '@type': 'Answer', text: faq.a },
                })),
              },
            ],
          },
        },
      ],
      links: [
        { rel: 'canonical', href: pageUrl },
        { rel: 'alternate', hrefLang: 'en', href: pageUrl },
        { rel: 'alternate', hrefLang: 'zh-HK', href: `${canonicalSiteUrl}/` },
        { rel: 'alternate', hrefLang: 'x-default', href: `${canonicalSiteUrl}/` },
        {
          rel: 'preload',
          as: 'image',
          href: '/images/doq-hero-arch-mobile.webp',
          media: '(max-width: 639px)',
          fetchPriority: 'high',
        },
        {
          rel: 'preload',
          as: 'image',
          href: '/images/doq-hero-arch-desktop.webp',
          media: '(min-width: 640px)',
          fetchPriority: 'high',
        },
      ],
    };
  },
  component: EnglishPage,
});
